/**
 * useMonthlyReceipts — 특정 연도의 1~12월 수금 집계 (홈 KPI 당월수금용).
 *
 * sales_receipts 를 수금일 [year-01-01, year-12-31] 범위로 일회성 조회 후
 * client-side 월별 누적. year별 in-memory 캐시 — 같은 year 재요청 시 0 reads.
 * (useMonthlySales 구조 복제)
 */
import { useEffect, useState } from "react";
import { useDataTenant } from "@/hooks/useTenantPath";
import type { TenantKey } from "@/domain/space";
import { salesRepo } from "@/services/sales.repository";

export interface MonthlyReceiptBucket {
  /** YYYY-MM */
  ym: string;
  /** 1~12 */
  month: number;
  /** 수금액 Σamount */
  amount: number;
}

/**
 * 캐시 키 = "법인:연도" — 법인 전환 시 다른 법인 집계가 섞이지 않게(2026-10-20 1단계).
 * ⚠️ 지금 조회(salesRepo)는 법인 구분 없는 중앙 컬렉션이라 경원 키에도 중앙 숫자가 담긴다(경원 공간은 "데이터 미연결" 안내 띠).
 *    2단계(tenants/{법인}/… 이전)에서 조회에 t 를 넘기면 키는 그대로 쓴다.
 */
const cache = new Map<string, MonthlyReceiptBucket[]>();

function initBuckets(year: number): MonthlyReceiptBucket[] {
  const arr: MonthlyReceiptBucket[] = [];
  for (let m = 1; m <= 12; m++) {
    arr.push({ ym: `${year}-${String(m).padStart(2, "0")}`, month: m, amount: 0 });
  }
  return arr;
}

export interface UseMonthlyReceiptsState {
  monthly: MonthlyReceiptBucket[];
  loading: boolean;
  error: string | null;
}

export function useMonthlyReceipts(year: number, tenant?: TenantKey): UseMonthlyReceiptsState {
  const dataTenant = useDataTenant();
  const t = tenant ?? dataTenant;
  const ck = `${t}:${year}`;
  // state 에 법인을 함께 둔다 — 법인이 바뀐 직후(새 조회 전) 이전 법인 숫자를 내보내지 않게
  const [state, setState] = useState<{ t: TenantKey; data: MonthlyReceiptBucket[] }>(
    () => ({ t, data: cache.get(ck) ?? initBuckets(year) }),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // year <= 0 → 비활성 (권한 게이팅 sentinel). fetch 안 함.
    if (year <= 0) {
      setState({ t, data: initBuckets(year) });
      return;
    }
    const cached = cache.get(ck);
    if (cached) {
      setState({ t, data: cached });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    salesRepo
      .fetchReceiptsBetween(`${year}-01-01`, `${year}-12-31`)
      .then((rows) => {
        if (cancelled) return;
        const buckets = initBuckets(year);
        for (const r of rows) {
          const mm = parseInt(r.date.slice(5, 7), 10);
          if (!mm || mm < 1 || mm > 12) continue;
          buckets[mm - 1].amount += r.amount;
        }
        for (const b of buckets) b.amount = Math.round(b.amount);
        cache.set(ck, buckets);
        setState({ t, data: buckets });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, ck, t]);

  const monthly = state.t === t ? state.data : initBuckets(year);
  return { monthly, loading, error };
}
